import type {
  CapturedPlaceholderValue,
  DispatchRecipientShape,
  WorkflowDefinition,
  WorkflowNode,
  WorkflowUserInputField,
} from '@corner/shared';
import { insertStepAfter } from './workflowGraphOps';

function randomStepSuffix(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return (crypto as Crypto & { randomUUID: () => string }).randomUUID().slice(0, 8);
  }
  return Math.random().toString(36).slice(2, 10);
}

/** True when the graph is only a document upload with no follow-on steps (server blank template). */
export function isBareDocumentInputWorkflow(def: WorkflowDefinition): boolean {
  return (
    def.graph.nodes.length === 1
    && def.graph.nodes[0]?.type === 'document_input'
    && def.graph.edges.length === 0
  );
}

/**
 * Inserts a first extraction step from plain language — closest on-ramp to NL→workflow
 * without a dedicated model endpoint (compare: “describe what you want” in other builders).
 */
export function seedFirstStepFromPlainLanguage(
  def: WorkflowDefinition,
  userDescription: string,
): WorkflowDefinition {
  const trimmed = userDescription.trim();
  if (!trimmed || !isBareDocumentInputWorkflow(def)) {
    return def;
  }
  const entryId = def.graph.entryNodeId;
  const taskNodeId = `extraction_task_${randomStepSuffix()}`;
  const newNode: WorkflowNode = {
    nodeId: taskNodeId,
    type: 'extraction_task',
    label: trimmed.length > 52 ? `${trimmed.slice(0, 49)}…` : trimmed,
    description: trimmed,
    config: {
      goal: trimmed,
      normalizedAction: 'extract_key_terms',
      inputKeys: ['source_document'],
      outputKey: 'result',
    },
  };
  const linked = insertStepAfter(def, entryId, newNode);
  const hasResultField = linked.outputSchema.fields.some((f) => f.key === 'result');
  return {
    ...linked,
    shortDescription: trimmed.slice(0, 240),
    outputSchema: hasResultField
      ? linked.outputSchema
      : {
          fields: [
            ...linked.outputSchema.fields,
            {
              key: 'result',
              label: 'Result',
              type: 'string',
              description: 'Workflow output',
              required: true,
            },
          ],
        },
  };
}

/**
 * Build a simple 2-node WorkflowDefinition from a completed analysis result.
 * Fully generic — works for any analysis type. The real intelligence
 * lives in the `goal` field, not in hardcoded node types.
 */
export function buildWorkflowFromResult(params: {
  analysisType: string;
  title: string;
  goal: string;
  fileName?: string;
}): WorkflowDefinition {
  const docNodeId = 'doc_input_1';
  const taskNodeId = 'task_1';

  return {
    workflowId: `user_wf_${Date.now()}`,
    version: 1,
    label: params.title,
    shortDescription: params.goal,
    longDescription: '',
    category: 'extraction',
    tags: [params.analysisType],
    status: 'draft',
    triggerType: 'manual',
    executionMode: 'semi_automated',
    approvalRequired: false,
    requiresDocument: true,
    minDocuments: 1,
    maxDocuments: 1,
    supportedDocuments: ['contract', 'lease', 'employment_agreement', 'general_document'],
    recommendedSources: ['local_upload', 'google_drive', 'dropbox'],
    exportHints: {
      preferredFormats: ['pdf', 'docx'],
      includeCitationsByDefault: false,
    },
    outputSchema: {
      fields: [
        { key: 'result', label: 'Result', type: 'string', description: 'The analysis output', required: true },
      ],
    },
    graph: {
      entryNodeId: docNodeId,
      nodes: [
        {
          nodeId: docNodeId,
          type: 'document_input',
          label: 'Upload document',
          description: params.fileName
            ? `Based on: ${params.fileName}`
            : 'Upload a document to process',
          config: {
            acceptedDocumentTypes: ['general_document'],
            allowMultiple: false,
            required: true,
            outputKey: 'source_document',
          },
        },
        {
          nodeId: taskNodeId,
          type: 'extraction_task',
          label: params.title,
          description: params.goal,
          config: {
            goal: params.goal,
            normalizedAction: 'extract_key_terms',
            inputKeys: ['source_document'],
            outputKey: 'result',
          },
        },
      ],
      edges: [
        {
          edgeId: 'edge_1',
          sourceNodeId: docNodeId,
          targetNodeId: taskNodeId,
          condition: null,
        },
      ],
    },
  };
}

// ── Contract velocity factory ───────────────────────────────────────
//
// Single source of truth for the contract-velocity graph shape:
//   clone_document → fill_placeholders → apply_signature → dispatch_for_signature
//
// Used by:
// - the backend capture endpoint (POST /api/workflows/from-contract-send) to
//   persist the workflow,
// - the frontend HUD panel as the canonical schema for v1.
//
// All recipient + delta keys are also added to suggestedUserInputs so the
// graph validator and HUD picker know they are required at run time.

export interface BuildContractWorkflowParams {
  /** Display label / workflow name. */
  title: string;
  /** Optional sub-title shown in the builder. */
  goal?: string;
  /** Original file name of the master PDF. */
  fileName?: string;
  /** ProcessedFile fileId of the master template. */
  sourceFileId: string;
  /** All baseline values captured from the original chat send. */
  baselineValues: CapturedPlaceholderValue[];
  /**
   * Recipient slots to capture. v1: typically a single recipient
   * ("primary"), but the shape supports multi-party.
   */
  recipients: DispatchRecipientShape[];
  /** Party role the workflow owner signs as (e.g. "buyer", "agent"). */
  signerRole: string;
  /** Optional contract category tag for the dashboard. */
  category?: WorkflowDefinition['category'];
  /** Optional message attached to dispatched signing requests. */
  dispatchMessage?: string;
  /** Optional expiry window for dispatched signing requests. */
  expiresInDays?: number;
}

export const CONTRACT_WORKFLOW_NODE_IDS = {
  clone: 'clone_1',
  fill: 'fill_1',
  sign: 'sign_1',
  dispatch: 'dispatch_1',
} as const;

export const CONTRACT_WORKFLOW_VARS = {
  clonedDoc: 'cloned_document',
  filledDoc: 'filled_document',
  signedDoc: 'signed_document',
  dispatchResult: 'dispatch_result',
} as const;

export function buildWorkflowFromContractSend(
  params: BuildContractWorkflowParams,
): WorkflowDefinition {
  const { clone, fill, sign, dispatch } = CONTRACT_WORKFLOW_NODE_IDS;
  const { clonedDoc, filledDoc, signedDoc, dispatchResult } = CONTRACT_WORKFLOW_VARS;

  // Delta keys come straight from the captured baseline values.
  const deltaBaselines = params.baselineValues.filter((v) => v.isDelta && v.deltaKey);
  const deltaKeys = Array.from(new Set(deltaBaselines.map((v) => v.deltaKey!).filter(Boolean)));

  // suggestedUserInputs lets the graph validator know these keys are inputs.
  const suggestedUserInputs: WorkflowUserInputField[] = [];
  for (const r of params.recipients) {
    suggestedUserInputs.push({
      key: r.emailKey,
      label: `${r.role} email`,
      type: 'string',
      required: true,
    });
    suggestedUserInputs.push({
      key: r.nameKey,
      label: `${r.role} name`,
      type: 'string',
      required: true,
    });
  }
  for (const dKey of deltaKeys) {
    const baseline = deltaBaselines.find((v) => v.deltaKey === dKey);
    suggestedUserInputs.push({
      key: dKey,
      label: baseline?.label ?? dKey,
      type: 'string',
      required: false,
    });
  }

  return {
    workflowId: `user_wf_${Date.now()}`,
    version: 1,
    label: params.title,
    shortDescription: params.goal ?? `Replay "${params.title}" with new recipients in seconds.`,
    longDescription:
      'Captured from a successful contract send. Surface the delta fields, click Finalize & Dispatch.',
    category: params.category ?? 'review',
    tags: ['contract', 'velocity'],
    status: 'draft',
    triggerType: 'manual',
    executionMode: 'automation_ready',
    approvalRequired: false,
    requiresDocument: false,
    supportedDocuments: ['contract', 'lease', 'employment_agreement', 'general_document'],
    recommendedSources: ['local_upload'],
    suggestedUserInputs,
    exportHints: {
      preferredFormats: ['pdf'],
      includeCitationsByDefault: false,
    },
    outputSchema: {
      fields: [
        {
          key: dispatchResult,
          label: 'Dispatch result',
          type: 'string',
          description: 'The signing request created when the workflow ran.',
          required: true,
        },
      ],
    },
    graph: {
      entryNodeId: clone,
      nodes: [
        {
          nodeId: clone,
          type: 'clone_document',
          label: 'Clone source document',
          description: params.fileName
            ? `Clone "${params.fileName}" so the original stays clean.`
            : 'Clone the master template so the original stays clean.',
          config: {
            sourceFileId: params.sourceFileId,
            outputKey: clonedDoc,
          },
        },
        {
          nodeId: fill,
          type: 'fill_placeholders',
          label: 'Fill placeholders',
          description: deltaKeys.length
            ? `Update ${deltaKeys.length} delta field${deltaKeys.length === 1 ? '' : 's'}; reuse the rest from baseline.`
            : 'Apply baseline values from the captured send.',
          config: {
            documentKey: clonedDoc,
            baselineValues: params.baselineValues,
            deltaKeys,
            outputKey: filledDoc,
            recomputeDates: true,
            signingRole: params.signerRole,
          },
        },
        {
          nodeId: sign,
          type: 'apply_signature',
          label: 'Apply your signature',
          description: `Sign as "${params.signerRole}" using your saved signature.`,
          config: {
            documentKey: filledDoc,
            signerRole: params.signerRole,
            outputKey: signedDoc,
            fieldType: 'signature',
          },
        },
        {
          nodeId: dispatch,
          type: 'dispatch_for_signature',
          label: 'Send for signature',
          description: `Dispatch to ${params.recipients.length} recipient${params.recipients.length === 1 ? '' : 's'}.`,
          config: {
            documentKey: signedDoc,
            recipients: params.recipients,
            outputKey: dispatchResult,
            message: params.dispatchMessage,
            expiresInDays: params.expiresInDays,
          },
        },
      ],
      edges: [
        { edgeId: 'edge_clone_fill', sourceNodeId: clone, targetNodeId: fill, condition: null },
        { edgeId: 'edge_fill_sign', sourceNodeId: fill, targetNodeId: sign, condition: null },
        { edgeId: 'edge_sign_dispatch', sourceNodeId: sign, targetNodeId: dispatch, condition: null },
      ],
    },
  };
}

/** True if a workflow uses the contract-velocity pattern (has a dispatch node). */
export function isContractVelocityWorkflow(definition: WorkflowDefinition | null | undefined): boolean {
  if (!definition) return false;
  return definition.graph.nodes.some((node) => node.type === 'dispatch_for_signature');
}
